import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../styles/Home.css';
import { FaCalendarAlt } from 'react-icons/fa';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const Topics = () => {
  const [categories, setCategories] = useState([]);
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const query = useQuery();
  const navigate = useNavigate();
  const categoryParam = query.get('category') || '';

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const base = (process.env.REACT_APP_BACKEND_API || '').trim();
        const baseUrl = base ? base.replace(/\/$/, '') : 'http://127.0.0.1:8000/api';

        const [catsRes, showsRes] = await Promise.all([
          fetch(`${baseUrl}/categories/`),
          fetch(`${baseUrl}/shows/`),
        ]);

        if (!catsRes.ok) throw new Error('Failed to load categories');
        if (!showsRes.ok) throw new Error('Failed to load shows');

        const catsData = await catsRes.json();
        const showsData = await showsRes.json();

        if (!mounted) return;

        const normalizedCats = (catsData || []).map(c => ({
          id: c.id,
          name: c.name || c.title || c.category_name || c.category || '',
          slug: c.slug || (c.name && c.name.toLowerCase().replace(/\s+/g,'-')),
        }));

        setCategories(normalizedCats);

        const publicShows = (showsData || []).filter(s => !s.is_private);
        setShows(publicShows);
        setLoading(false);
      } catch (err) {
        if (!mounted) return;
        setError(err.message || 'Failed to load');
        setLoading(false);
      }
    };

    fetchAll();
    return () => { mounted = false; };
  }, [categoryParam]);

  // Find category id(s) that match the categoryParam (slug or name or id)
  const matchedCategoryIds = React.useMemo(() => {
    if (!categoryParam) return [];
    const lower = categoryParam.toLowerCase();
    return categories
      .filter(c => (c.slug && c.slug.toLowerCase() === lower) || (c.name && c.name.toLowerCase() === lower) || String(c.id) === categoryParam)
      .map(c => c.id);
  }, [categories, categoryParam]);

  // Lookup maps for quick name resolution
  const categoriesById = React.useMemo(() => {
    const m = {};
    categories.forEach(c => {
      if (c?.id != null) m[String(c.id)] = c.name;
      if (c?.id != null) m[c.id] = c.name;
    });
    return m;
  }, [categories]);

  // Find selected category object to display its backend name (preserve case)
  const selectedCategory = React.useMemo(() => {
    if (!categoryParam) return null;
    const lower = categoryParam.toLowerCase();
    return categories.find(c => (c.slug && c.slug.toLowerCase() === lower) || (c.name && c.name.toLowerCase() === lower) || String(c.id) === categoryParam) || null;
  }, [categories, categoryParam]);

  const getCategoryDisplay = (show) => {
    // show.category may be id or name; try id lookup first
    const catId = show.category || show.category_id || show.categoryId;
    if (catId != null) {
      const found = categoriesById[String(catId)];
      if (found) return found;
    }
    // fallback to any provided category_name or category string on the show
    if (show.category_name) return show.category_name;
    if (show.category && typeof show.category === 'string') return show.category;
    return '';
  };

  const filteredShows = React.useMemo(() => {
    if (!categoryParam) return shows;
    // If some categories matched, filter by those ids
    if (matchedCategoryIds.length) {
      return shows.filter(s => matchedCategoryIds.includes(s.category) || matchedCategoryIds.includes(s.category_id) || matchedCategoryIds.includes(Number(s.category) || Number(s.category_id)) );
    }
    // fallback: match by category name/slug string on the show
    const lower = categoryParam.toLowerCase();
    return shows.filter(s => {
      const catName = s.category_name || s.category || '';
      return String(catName).toLowerCase() === lower || (s.tags && s.tags.some(t => String(t).toLowerCase() === lower));
    });
  }, [shows, categoryParam, matchedCategoryIds]);

  return (
    <div className="home-wrapper">
  <h3 style={{ marginBottom: 40 ,marginTop: 24 }}>{selectedCategory ? `${selectedCategory.name}` : (categoryParam ? `Category: ${categoryParam}` : 'All Topics')}</h3>

      {loading && <div className="loading-block">Loading...</div>}
      {error && <div className="error-block">{error}</div>}

      {!loading && !error && (
        <div>
          {filteredShows.length === 0 ? (
            <div className="muted">No shows found for this category.</div>
          ) : (
            <div className="more-stories">
              {filteredShows.map(s => {
                const sub = s.subtopics?.[0];
                return (
                  <article key={s.id} className="grid-tile" onClick={() => navigate(`/read-more/${s.id}`)}>
                    {sub?.image_urls?.[0] ? <img src={sub.image_urls[0]} alt={s.title} /> : <div className="placeholder-img" />}
                    <div className="grid-text">
                      <div className="tile-head">
                        {getCategoryDisplay(s) && <span className="category-label">{getCategoryDisplay(s)}</span>}
                        <h4 dangerouslySetInnerHTML={{ __html: s.title }} />
                      </div>
                      <p className="meta"><FaCalendarAlt /> {s.show_date}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Topics;
